import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    Settings as ActuatorIcon,
    Stop as StopIcon,
    ReportProblem as WarningIcon,
} from '@mui/icons-material';
import ConfirmationDialog from './ConfirmationDialog';

// --- SERVOn_FUNCTION dictionary (ArduPilot SRV_Channel::Aux_servo_function_t) ---
// Curated common set; unknown numbers fall back to "Func N".
const SERVO_FUNCTION_NAMES = {
    0: 'Disabled',
    1: 'RCPassThru',
    2: 'Flap',
    3: 'FlapAuto',
    4: 'Aileron',
    6: 'Mount1Yaw',
    7: 'Mount1Pitch',
    8: 'Mount1Roll',
    9: 'Mount1Open',
    10: 'CameraTrigger',
    12: 'Mount2Yaw',
    13: 'Mount2Pitch',
    14: 'Mount2Roll',
    15: 'Mount2Open',
    16: 'DifferentialSpoilerL1',
    17: 'DifferentialSpoilerR1',
    18: 'AileronWithInput',
    19: 'Elevator',
    20: 'ElevatorWithInput',
    21: 'Rudder',
    22: 'SprayerPump',
    23: 'SprayerSpinner',
    24: 'FlaperonLeft',
    25: 'FlaperonRight',
    26: 'GroundSteering',
    27: 'Parachute',
    28: 'Gripper',
    29: 'LandingGear',
    30: 'EngineRunEnable',
    31: 'HeliRSC',
    32: 'HeliTailRSC',
    33: 'Motor1',
    34: 'Motor2',
    35: 'Motor3',
    36: 'Motor4',
    37: 'Motor5',
    38: 'Motor6',
    39: 'Motor7',
    40: 'Motor8',
    41: 'MotorTilt',
    51: 'RCIN1',
    52: 'RCIN2',
    53: 'RCIN3',
    54: 'RCIN4',
    55: 'RCIN5',
    56: 'RCIN6',
    57: 'RCIN7',
    58: 'RCIN8',
    59: 'RCIN9',
    60: 'RCIN10',
    61: 'RCIN11',
    62: 'RCIN12',
    63: 'RCIN13',
    64: 'RCIN14',
    65: 'RCIN15',
    66: 'RCIN16',
    67: 'Ignition',
    69: 'Starter',
    70: 'Throttle',
    71: 'TrackerYaw',
    72: 'TrackerPitch',
    73: 'ThrottleLeft',
    74: 'ThrottleRight',
    75: 'TiltMotorsFront',
    76: 'TiltMotorsRear',
    77: 'TiltMotorsRearLeft',
    78: 'TiltMotorsRearRight',
    79: 'ElevonLeft',
    80: 'ElevonRight',
    81: 'VTailLeft',
    82: 'VTailRight',
    83: 'BoostThrottle',
    84: 'Motor9',
    85: 'Motor10',
    86: 'Motor11',
    87: 'Motor12',
    88: 'DifferentialSpoilerL2',
    89: 'DifferentialSpoilerR2',
    90: 'WingSailElevator',
    91: 'ProfiLED1',
    92: 'ProfiLED2',
    93: 'ProfiLED3',
    94: 'ProfiLEDClock',
    95: 'Winch',
    96: 'MainSail',
    97: 'CameraISO',
    98: 'CameraAperture',
    99: 'CameraFocus',
    100: 'CameraShutterSpeed',
    101: 'Script1',
    102: 'Script2',
    103: 'Script3',
    104: 'Script4',
    105: 'Script5',
    106: 'Script6',
    107: 'Script7',
    108: 'Script8',
    109: 'Script9',
    110: 'Script10',
    111: 'Script11',
    112: 'Script12',
    113: 'Script13',
    114: 'Script14',
    115: 'Script15',
    116: 'Script16',
    120: 'NeoPixel1',
    121: 'NeoPixel2',
    122: 'NeoPixel3',
    123: 'NeoPixel4',
    124: 'RateRoll',
    125: 'RatePitch',
    126: 'RateThrust',
    127: 'RateYaw',
    129: 'WingSailMastRotation',
    130: 'Alarm',
    131: 'AlarmInverted',
    132: 'RCIN1Scaled',
    133: 'RCIN2Scaled',
    134: 'RCIN3Scaled',
    135: 'RCIN4Scaled',
    136: 'RCIN5Scaled',
    137: 'RCIN6Scaled',
    138: 'RCIN7Scaled',
    139: 'RCIN8Scaled',
    140: 'RCIN9Scaled',
    141: 'RCIN10Scaled',
    142: 'RCIN11Scaled',
    143: 'RCIN12Scaled',
    144: 'RCIN13Scaled',
    145: 'RCIN14Scaled',
    146: 'RCIN15Scaled',
    147: 'RCIN16Scaled',
};

// Options for the Autocomplete dropdown
const FUNCTION_OPTIONS = Object.entries(SERVO_FUNCTION_NAMES)
    .map(([k, v]) => ({ value: Number(k), label: `${v} (${k})` }))
    .sort((a, b) => a.value - b.value);

const optionForValue = (v) => {
    if (v == null) return null;
    const n = Number(v);
    const known = SERVO_FUNCTION_NAMES[n];
    return { value: n, label: known ? `${known} (${n})` : `Func ${n}` };
};

const getParamEntry = (mavId, name) => {
    const list = window.paramList && window.paramList[mavId];
    if (!list || !list[name]) return null;
    return list[name];
};
const getParamValue = (mavId, name) => {
    const e = getParamEntry(mavId, name);
    return e ? e.value : null;
};

// Absolute PWM range used to lay out the per-channel bar so MIN/TRIM/MAX/VALUE
// cursors move visually as the inputs change.
const PWM_LO = 800;
const PWM_HI = 2200;
const PWM_SPAN = PWM_HI - PWM_LO;
const pwmPct = (v) => {
    if (v == null || Number.isNaN(Number(v))) return null;
    return Math.min(100, Math.max(0, ((Number(v) - PWM_LO) / PWM_SPAN) * 100));
};

const NumericInput = ({ valueProp, disabled, onCommit }) => {
    const [text, setText] = useState(valueProp == null ? '' : String(valueProp));
    const lastPropRef = React.useRef(valueProp);
    useEffect(() => {
        if (valueProp !== lastPropRef.current) {
            lastPropRef.current = valueProp;
            setText(valueProp == null ? '' : String(valueProp));
        }
    }, [valueProp]);
    const commit = () => {
        if (text === '' || text == null) return;
        const n = Number(text);
        if (Number.isNaN(n)) {
            setText(valueProp == null ? '' : String(valueProp));
            return;
        }
        if (n !== Number(valueProp)) onCommit(n);
    };
    return (
        <TextField
            size="small"
            value={text}
            disabled={disabled}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') { e.target.blur(); }
                else if (e.key === 'Escape') { setText(valueProp == null ? '' : String(valueProp)); e.target.blur(); }
            }}
            inputProps={{ inputMode: 'numeric', style: { textAlign: 'right', padding: '4px 6px', fontSize: 12, fontVariantNumeric: 'tabular-nums' } }}
            sx={{ width: '100%', minWidth: 0, '& .MuiInputBase-root': { fontSize: 12 } }}
        />
    );
};

const ServoRow = React.memo(({ index, pwm, min, max, trim, func, canBuses, theme, disabled, onChangeFunction, onChangeLimit, onToggleCan }) => {
    const valNum = pwm == null ? null : Number(pwm);
    const inactive = pwm == null || pwm === 65535;
    const minPct = pwmPct(min);
    const maxPct = pwmPct(max);
    const trimPct = pwmPct(trim);
    const valPct = inactive ? null : pwmPct(valNum);
    const funcNum = func == null ? '' : Number(func);
    const isKnown = funcNum !== '' && SERVO_FUNCTION_NAMES[funcNum] !== undefined;

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '60px 220px 100px 100px 60px 60px 60px minmax(160px, 1fr) 64px',
                alignItems: 'center',
                columnGap: 1.5,
                py: 0.5,
                px: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>CH {index}</Typography>

            <Select
                size="small"
                value={funcNum}
                disabled={disabled}
                displayEmpty
                onChange={(e) => {
                    const v = Number(e.target.value);
                    if (v !== Number(func)) onChangeFunction(index, v);
                }}
                MenuProps={{
                    PaperProps: { sx: { maxHeight: 360, fontSize: 12 } },
                    disableScrollLock: true,
                }}
                sx={{ fontSize: 12, '& .MuiSelect-select': { py: 0.5 } }}
                renderValue={(v) => {
                    if (v === '' || v == null) return <em style={{ opacity: 0.6 }}>—</em>;
                    const name = SERVO_FUNCTION_NAMES[v];
                    return name ? `${name} (${v})` : `Func ${v}`;
                }}
            >
                {!isKnown && funcNum !== '' && (
                    <MenuItem value={funcNum}>Func {funcNum}</MenuItem>
                )}
                {FUNCTION_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} dense>
                        {opt.label}
                    </MenuItem>
                ))}
            </Select>

            {canBuses.map((bus) => (
                <Box key={bus.id} sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                    {bus.escAvail ? (
                        <Tooltip title={disabled ? 'Enable edit to toggle' : (bus.canEsc ? `Click to disable CAN${bus.id} ESC on this channel` : `Click to enable CAN${bus.id} ESC on this channel`)} placement="top" arrow>
                            <span>
                                <Chip
                                    label="ESC"
                                    size="small"
                                    color={bus.canEsc ? 'primary' : 'default'}
                                    variant={bus.canEsc ? 'filled' : 'outlined'}
                                    onClick={disabled ? undefined : () => onToggleCan(index, bus.id, 'ESC')}
                                    sx={{
                                        height: 18,
                                        fontSize: 10,
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        opacity: disabled && !bus.canEsc ? 0.5 : 1,
                                        '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                            </span>
                        </Tooltip>
                    ) : null}
                    {bus.srvAvail ? (
                        <Tooltip title={disabled ? 'Enable edit to toggle' : (bus.canSrv ? `Click to disable CAN${bus.id} SRV on this channel` : `Click to enable CAN${bus.id} SRV on this channel`)} placement="top" arrow>
                            <span>
                                <Chip
                                    label="SRV"
                                    size="small"
                                    color={bus.canSrv ? 'secondary' : 'default'}
                                    variant={bus.canSrv ? 'filled' : 'outlined'}
                                    onClick={disabled ? undefined : () => onToggleCan(index, bus.id, 'SRV')}
                                    sx={{
                                        height: 18,
                                        fontSize: 10,
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        opacity: disabled && !bus.canSrv ? 0.5 : 1,
                                        '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                            </span>
                        </Tooltip>
                    ) : null}
                    {!bus.escAvail && !bus.srvAvail && (
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>
                    )}
                </Box>
            ))}

            <NumericInput
                valueProp={min}
                disabled={disabled}
                onCommit={(v) => onChangeLimit(index, 'MIN', v)}
            />
            <NumericInput
                valueProp={trim}
                disabled={disabled}
                onCommit={(v) => onChangeLimit(index, 'TRIM', v)}
            />
            <NumericInput
                valueProp={max}
                disabled={disabled}
                onCommit={(v) => onChangeLimit(index, 'MAX', v)}
            />

            <Tooltip title={`${PWM_LO} — [min ${min ?? '—'} / trim ${trim ?? '—'} / max ${max ?? '—'}] — ${PWM_HI}`} placement="top" arrow>
                <Box sx={{ position: 'relative', height: 18, display: 'flex', alignItems: 'center' }}>
                    {/* Rail: full PWM_LO..PWM_HI */}
                    <Box sx={{ position: 'absolute', left: 0, right: 0, height: 4, borderRadius: 1, bgcolor: theme.palette.action.hover }} />
                    {/* Configured range stripe min..max */}
                    {minPct != null && maxPct != null && maxPct > minPct && (
                        <Box sx={{
                            position: 'absolute',
                            left: `${minPct}%`,
                            width: `${maxPct - minPct}%`,
                            height: 4,
                            borderRadius: 1,
                            bgcolor: theme.palette.primary.dark,
                            opacity: 0.5,
                        }} />
                    )}
                    {/* Current value fill from min to value */}
                    {!inactive && minPct != null && valPct != null && valPct >= minPct && (
                        <Box sx={{
                            position: 'absolute',
                            left: `${minPct}%`,
                            width: `${Math.max(0, valPct - minPct)}%`,
                            height: 4,
                            borderRadius: 1,
                            bgcolor: theme.palette.primary.main,
                        }} />
                    )}
                    {/* MIN cursor */}
                    {minPct != null && (
                        <Box sx={{
                            position: 'absolute', left: `${minPct}%`, top: 0, bottom: 0,
                            width: 0, borderLeft: `1px solid ${theme.palette.text.secondary}`,
                        }} />
                    )}
                    {/* MAX cursor */}
                    {maxPct != null && (
                        <Box sx={{
                            position: 'absolute', left: `${maxPct}%`, top: 0, bottom: 0,
                            width: 0, borderLeft: `1px solid ${theme.palette.text.secondary}`,
                        }} />
                    )}
                    {/* TRIM cursor (dashed) */}
                    {trimPct != null && (
                        <Box sx={{
                            position: 'absolute', left: `${trimPct}%`, top: 0, bottom: 0,
                            width: 0, borderLeft: `1px dashed ${theme.palette.warning.main}`,
                        }} />
                    )}
                </Box>
            </Tooltip>

            <Typography
                variant="caption"
                sx={{
                    textAlign: 'right',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    fontVariantNumeric: 'tabular-nums',
                    color: inactive ? 'text.disabled' : 'text.primary',
                }}
            >
                {inactive ? '—' : valNum}
            </Typography>
        </Box>
    );
});

const Actuators = ({ selectedMavId, mavTimestamp, paramListTimestamp }) => {
    const theme = useTheme();
    const [servoOutput, setServoOutput] = useState(null);
    const [editEnabled, setEditEnabled] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [motorTestEnabled, setMotorTestEnabled] = useState(false);
    const [motorTestConfirmOpen, setMotorTestConfirmOpen] = useState(false);
    const [motorThrottle, setMotorThrottle] = useState(8);
    const [motorDuration, setMotorDuration] = useState(2);

    useEffect(() => {
        if (!window.mav) return;
        const vehicle = window.mav[selectedMavId];
        if (!vehicle) return;
        if (vehicle.SERVO_OUTPUT_RAW) setServoOutput(vehicle.SERVO_OUTPUT_RAW);
    }, [mavTimestamp, selectedMavId]);

    const escEntry1 = getParamEntry(selectedMavId, 'CAN_D1_UC_ESC_BM');
    const srvEntry1 = getParamEntry(selectedMavId, 'CAN_D1_UC_SRV_BM');
    const escEntry2 = getParamEntry(selectedMavId, 'CAN_D2_UC_ESC_BM');
    const srvEntry2 = getParamEntry(selectedMavId, 'CAN_D2_UC_SRV_BM');
    const escAvail1 = !!escEntry1;
    const srvAvail1 = !!srvEntry1;
    const escAvail2 = !!escEntry2;
    const srvAvail2 = !!srvEntry2;

    const channels = useMemo(() => {
        const toBm = (raw) => (Number.isFinite(Number(raw)) ? (Number(raw) >>> 0) : 0);
        const escBm1 = toBm(getParamValue(selectedMavId, 'CAN_D1_UC_ESC_BM'));
        const srvBm1 = toBm(getParamValue(selectedMavId, 'CAN_D1_UC_SRV_BM'));
        const escBm2 = toBm(getParamValue(selectedMavId, 'CAN_D2_UC_ESC_BM'));
        const srvBm2 = toBm(getParamValue(selectedMavId, 'CAN_D2_UC_SRV_BM'));
        const arr = [];
        for (let i = 1; i <= 16; i++) {
            const key = `servo${i}_raw`;
            const pwm = servoOutput ? servoOutput[key] : 0;
            const min = getParamValue(selectedMavId, `SERVO${i}_MIN`);
            const max = getParamValue(selectedMavId, `SERVO${i}_MAX`);
            const trim = getParamValue(selectedMavId, `SERVO${i}_TRIM`);
            const func = getParamValue(selectedMavId, `SERVO${i}_FUNCTION`);
            const bit = 1 << (i - 1);
            const canBuses = [
                { id: 1, escAvail: escAvail1, srvAvail: srvAvail1, canEsc: (escBm1 & bit) !== 0, canSrv: (srvBm1 & bit) !== 0 },
                { id: 2, escAvail: escAvail2, srvAvail: srvAvail2, canEsc: (escBm2 & bit) !== 0, canSrv: (srvBm2 & bit) !== 0 },
            ];
            arr.push({ i, pwm, min, max, trim, func, canBuses });
        }
        return arr;
    }, [servoOutput, selectedMavId, paramListTimestamp, escAvail1, srvAvail1, escAvail2, srvAvail2]);

    const isActivePwm = (pwm) => pwm != null && pwm !== 65535;
    const isConfigured = (c) => (c.func != null && Number(c.func) !== 0) || isActivePwm(c.pwm);

    const visibleChannels = channels;

    const configuredCount = channels.filter(isConfigured).length;
    const activeCount = channels.filter((c) => isActivePwm(c.pwm)).length;

    const handleChangeFunction = useCallback((index, newFunc) => {
        const name = `SERVO${index}_FUNCTION`;
        const entry = getParamEntry(selectedMavId, name);
        if (!entry) return;
        window.api.setParams(selectedMavId, {
            [name]: { value: newFunc, type: entry.type },
        });
    }, [selectedMavId]);

    const handleChangeLimit = useCallback((index, suffix, newVal) => {
        const name = `SERVO${index}_${suffix}`;
        const entry = getParamEntry(selectedMavId, name);
        if (!entry) return;
        window.api.setParams(selectedMavId, {
            [name]: { value: newVal, type: entry.type },
        });
    }, [selectedMavId]);

    const handleToggleCan = useCallback((index, busId, kind) => {
        const name = `CAN_D${busId}_UC_${kind}_BM`;
        const entry = getParamEntry(selectedMavId, name);
        if (!entry) return;
        const raw = entry.value;
        const current = Number.isFinite(Number(raw)) ? (Number(raw) >>> 0) : 0;
        const bit = 1 << (index - 1);
        const next = (current ^ bit) >>> 0;
        window.api.setParams(selectedMavId, {
            [name]: { value: next, type: entry.type },
        });
    }, [selectedMavId]);

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                p: '2px',
                boxSizing: 'border-box',
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    overflow: 'hidden',
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <ActuatorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="subtitle2">Servo Output</Typography>
                    <Chip
                        size="small"
                        label={`${configuredCount} configured`}
                        color={configuredCount > 0 ? 'primary' : 'default'}
                        sx={{ height: 20, fontSize: 11 }}
                    />
                    <Chip
                        size="small"
                        label={`${activeCount} active`}
                        color={activeCount > 0 ? 'success' : 'default'}
                        sx={{ height: 20, fontSize: 11 }}
                    />
                    {!servoOutput && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Waiting for SERVO_OUTPUT_RAW…
                        </Typography>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={editEnabled}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setConfirmOpen(true);
                                    } else {
                                        setEditEnabled(false);
                                    }
                                }}
                            />
                        }
                        label={<Typography variant="caption">Enable edit</Typography>}
                        sx={{ mr: 0 }}
                    />
                </Stack>

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        bgcolor: 'background.default',
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '60px 220px 100px 100px 60px 60px 60px minmax(160px, 1fr) 64px',
                            columnGap: 1.5,
                            px: 1,
                            py: 0.75,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            color: 'text.secondary',
                            position: 'sticky',
                            top: 0,
                            bgcolor: 'background.default',
                            zIndex: 1,
                        }}
                    >
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>Channel</Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>Function</Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>CAN D1</Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>CAN D2</Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>Min</Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>Trim</Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>Max</Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>Output</Typography>
                        <Typography variant="caption" sx={{ textAlign: 'center' }}>Value</Typography>
                    </Box>
                    {visibleChannels.map((c) => (
                        <ServoRow
                            key={c.i}
                            index={c.i}
                            pwm={c.pwm}
                            min={c.min}
                            max={c.max}
                            trim={c.trim}
                            func={c.func}
                            canBuses={c.canBuses}
                            theme={theme}
                            disabled={!editEnabled || !selectedMavId || !getParamEntry(selectedMavId, `SERVO${c.i}_FUNCTION`)}
                            onChangeFunction={handleChangeFunction}
                            onChangeLimit={handleChangeLimit}
                            onToggleCan={handleToggleCan}
                        />
                    ))}
                </Box>

                {/* ---- Motor Test ---- */}
                <Paper
                    variant="outlined"
                    sx={{ p: 1.5, mt: 0.5 }}
                >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <WarningIcon fontSize="small" sx={{ color: 'warning.main' }} />
                        <Typography variant="subtitle2">Motor Test</Typography>
                        <Box sx={{ flex: 1 }} />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={motorTestEnabled}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setMotorTestConfirmOpen(true);
                                        } else {
                                            setMotorTestEnabled(false);
                                        }
                                    }}
                                />
                            }
                            label={<Typography variant="caption">Enable motor test</Typography>}
                            sx={{ mr: 0 }}
                        />
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="stretch" flexWrap="nowrap">
                        <Paper variant="outlined" sx={{ flex: 1, p: 1, display: 'flex', flexDirection: 'column', gap: 1, borderColor: 'warning.main' }}>
                            <Typography variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <WarningIcon sx={{ fontSize: 14 }} />
                                Remove propellers before spinning motors. Keep throttle low and duration short.
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="nowrap" sx={{ width: '100%' }}>
                                {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((letter, idx) => {
                                    const motor = idx + 1;
                                    return (
                                        <Tooltip key={letter} title={`Test motor ${letter} (${motor})`}>
                                            <span>
                                                <Button
                                                    variant="contained"
                                                    color="warning"
                                                    size="small"
                                                    disabled={!motorTestEnabled || !selectedMavId}
                                                    onClick={() => {
                                                        window.api.motorTest(
                                                            selectedMavId,
                                                            motor,
                                                            motorThrottle,
                                                            motorDuration,
                                                            { throttleType: 0, motorCount: 0, testOrder: 0 },
                                                        );
                                                    }}
                                                    sx={{ minWidth: 40, fontWeight: 700 }}
                                                >
                                                    {letter}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    );
                                })}
                                <Tooltip title="Test all 8 motors sequentially (A→H), each for Duration seconds">
                                    <span>
                                        <Button
                                            variant="contained"
                                            color="warning"
                                            size="small"
                                            disabled={!motorTestEnabled || !selectedMavId}
                                            onClick={() => {
                                                window.api.motorTest(
                                                    selectedMavId,
                                                    1,
                                                    motorThrottle,
                                                    motorDuration,
                                                    { throttleType: 0, motorCount: 8, testOrder: 0 },
                                                );
                                            }}
                                            sx={{ minWidth: 52, fontWeight: 700 }}
                                        >
                                            ALL
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Box sx={{ flex: 1, minWidth: 8 }} />
                                <TextField
                                    label="Throttle (%)"
                                    type="number"
                                    size="small"
                                    value={motorThrottle}
                                    onChange={(e) => {
                                        const n = parseFloat(e.target.value);
                                        setMotorThrottle(Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0);
                                    }}
                                    inputProps={{ min: 0, max: 100, step: 1 }}
                                    sx={{ width: 110, flexShrink: 0 }}
                                    disabled={!motorTestEnabled || !selectedMavId}
                                />
                                <TextField
                                    label="Duration (s)"
                                    type="number"
                                    size="small"
                                    value={motorDuration}
                                    onChange={(e) => {
                                        const n = parseFloat(e.target.value);
                                        setMotorDuration(Number.isFinite(n) ? Math.max(0, Math.min(30, n)) : 0);
                                    }}
                                    inputProps={{ min: 0, max: 30, step: 0.5 }}
                                    sx={{ width: 110, flexShrink: 0 }}
                                    disabled={!motorTestEnabled || !selectedMavId}
                                />
                            </Stack>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexShrink: 0 }}>
                            <Tooltip title="Stop all motor tests">
                                <span style={{ display: 'flex' }}>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<StopIcon />}
                                        disabled={!motorTestEnabled || !selectedMavId}
                                        onClick={() => {
                                            window.api.motorTestStop(selectedMavId, 0);
                                        }}
                                        sx={{ minWidth: 160, height: '100%', fontWeight: 800, fontSize: 16 }}
                                    >
                                        Stop All
                                    </Button>
                                </span>
                            </Tooltip>
                        </Paper>
                    </Stack>
                </Paper>
            </Box>

            <ConfirmationDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => { setEditEnabled(true); setConfirmOpen(false); }}
                title="Enable servo editing?"
                description="Modifying SERVOx_FUNCTION / MIN / TRIM / MAX while the vehicle is armed or in flight can cause unexpected actuator behavior and crash. Only proceed if the vehicle is safely on the ground with props removed or motors disabled."
                confirmColor="warning"
            />
            <ConfirmationDialog
                open={motorTestConfirmOpen}
                onClose={() => setMotorTestConfirmOpen(false)}
                onConfirm={() => { setMotorTestEnabled(true); setMotorTestConfirmOpen(false); }}
                title="Enable motor test?"
                description="Motor test will spin the selected motor at the configured throttle. ALWAYS remove propellers and ensure the vehicle is secured before proceeding."
                confirmColor="warning"
            />
        </Box>
    );
};

export default Actuators;
